/**
 * Authentication Context - Manages user authentication state
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';
import documentRequirementsService from '../services/documentRequirementsService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const logout = useCallback(() => {
    apiService.logout();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const checkTokenExpiration = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;

        if (payload.exp < currentTime) {
          // Token expired
          logout();
          window.location.href = '/auth';
        }
      } catch (error) {
        console.error('Error checking token expiration:', error);
        logout();
      }
    }
  }, [logout]);

  const checkAuthStatus = useCallback(async () => {
    try {
      if (apiService.isAuthenticated()) {
        // Check if token is expired before making API call
        checkTokenExpiration();

        const userData = await apiService.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Clear invalid token
      apiService.logout();
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, [checkTokenExpiration]);

  const login = async (email, password) => {
    try {
      const response = await apiService.login(email, password);
      const userData = await apiService.getCurrentUser();
      
      setUser(userData);
      setIsAuthenticated(true);
      
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const registerStep1 = async (data) => {
    try {
      const response = await apiService.registerStep1(data);
      
      // After successful registration step 1, get user data
      const userData = await apiService.getCurrentUser();
      setUser(userData);
      setIsAuthenticated(true);
      
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const registerStep2 = async (data) => {
    try {
      const response = await apiService.registerStep2(data);
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const registerStep4 = async (data) => {
    try {
      const response = await apiService.registerStep4(data);

      // Refresh user data after step 4 (but registration not complete yet)
      const userData = await apiService.getCurrentUser();
      setUser(userData);

      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const registerStep5 = async (data) => {
    try {
      const response = await apiService.registerStep5(data);

      // Refresh user data after completing registration
      const userData = await apiService.getCurrentUser();
      setUser(userData);

      // Handle document requirements assignment for new registrations
      try {
        await documentRequirementsService.handleProgramRegistration(
          'general_registration' // Default program for general registration
        );
      } catch (docError) {
        console.warn('Failed to assign document requirements after registration:', docError);
        // Don't fail the registration if document assignment fails
      }

      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const getRegistrationProgress = async () => {
    try {
      return await apiService.getRegistrationProgress();
    } catch (error) {
      console.error('Failed to get registration progress:', error);
      return null;
    }
  };

  const getFamilyInfo = async () => {
    try {
      return await apiService.getFamilyInfo();
    } catch (error) {
      console.error('Failed to get family info:', error);
      return null;
    }
  };

  const saveAndExit = async (step, data) => {
    try {
      const response = await apiService.saveAndExit(step, data);
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Helper methods for registration status
  const isRegistrationComplete = () => {
    // Check if registration is complete using nested registration object
    // registration.is_complete is true when all steps are finished
    return user?.registration?.is_complete === true;
  };

  const hasPartialRegistration = () => {
    // Check if registration has been started but not completed
    return user?.registration && !user.registration.is_complete;
  };

  const needsPlayerRegistration = () => {
    // This will be implemented when we add player functionality
    return isRegistrationComplete() && !user?.has_players;
  };

  // Document Requirements Helper Functions
  const checkProcessGate = async (processType, processId) => {
    if (!user) return { can_proceed: false, message: 'User not authenticated' };

    try {
      return await documentRequirementsService.checkProcessGate(processType, processId);
    } catch (error) {
      console.error('Error checking process gate:', error);
      return { can_proceed: false, message: 'Error checking requirements' };
    }
  };

  const getUserRequirementsSummary = async () => {
    if (!user) return null;

    try {
      return await documentRequirementsService.getMySummary();
    } catch (error) {
      console.error('Error getting requirements summary:', error);
      return null;
    }
  };

  const getUserAssignments = async (statusFilter = null) => {
    if (!user) return [];

    try {
      return await documentRequirementsService.getMyAssignments(statusFilter);
    } catch (error) {
      console.error('Error getting user assignments:', error);
      return [];
    }
  };

  // Check if user is authenticated on app load
  useEffect(() => {
    checkAuthStatus();

    // Set up token expiration check
    const interval = setInterval(checkTokenExpiration, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [checkAuthStatus, checkTokenExpiration]);

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    registerStep1,
    registerStep2,
    registerStep4,
    registerStep5,
    getRegistrationProgress,
    getFamilyInfo,
    saveAndExit,
    checkAuthStatus,
    isRegistrationComplete,
    hasPartialRegistration,
    needsPlayerRegistration,
    // Document Requirements
    checkProcessGate,
    getUserRequirementsSummary,
    getUserAssignments
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
